import type { Dispatch, SetStateAction } from 'react';

import type { Table } from '@tanstack/react-table';
import { Box } from '@/shared/ui';
import { FilterAltOutlined } from '@/shared/ui/icons';

import type { CatalogItem } from '@/entities/catalog-item';
import Button from '@/shared/ui/primitives/Button';

import { useFilter } from '../../state/useFilter';

// TODO(fuzzy-search-tuning): remove this import once tuning params are finalized
import { FuzzySearchSettingsPanel } from '../../fuzzy-search-tuning';
import DisplaySettingsPopper from './DisplaySettings/DisplaySettings';
import TableSearch from './TableSearch/TableSearch';

// TODO(GO-2392 follow-up): the redesign drops the "Show selected data" switch
// (./ShowSelectedSwitch) and the "Select All" button
// (./SelectAllButton/SelectAllButton) from the toolbar. Both components and
// their logic are kept for re-homing in a later ticket — re-add them here (or
// wherever the new design places them) when that work lands.

type CatalogToolbarProps = {
  filterOpen: boolean;
  setFilterOpen: (filterOpen: boolean) => void;
  table: Table<CatalogItem>;
  isForwardCurves: boolean;
  expandedData: boolean;
  setExpandedData: (newValue: boolean) => void;
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
};

const CatalogToolbar = ({
  filterOpen,
  setFilterOpen,
  table,
  isForwardCurves,
  expandedData,
  setExpandedData,
  showDiscontinued,
  setShowDiscontinued
}: CatalogToolbarProps) => {
  const { isAnyFilterApplied } = useFilter();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        height: 64,
        pl: 0,
        pr: 2,
        backgroundColor: 'gx.mainBackground',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'gx.actionBarButtonTile'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          size='small'
          sx={{ width: 171, height: 32, px: 1, justifyContent: 'flex-start' }}
          variant='outlined'
          onClick={() => {
            setFilterOpen(!filterOpen);
          }}
        >
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
            <FilterAltOutlined sx={{ mr: 0.6 }} />
            {isAnyFilterApplied ? (
              <>
                Filters Applied
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: 'gx.highlight',
                    mx: 1
                  }}
                />
              </>
            ) : (
              'Filter'
            )}
          </Box>
        </Button>
        <DisplaySettingsPopper
          expandedData={expandedData}
          isForwardCurves={isForwardCurves}
          setExpandedData={setExpandedData}
          setShowDiscontinued={setShowDiscontinued}
          showDiscontinued={showDiscontinued}
          table={table}
        />
        {/* TODO(fuzzy-search-tuning): remove once tuning params are finalized */}
        <FuzzySearchSettingsPanel />
      </Box>
      <TableSearch tabId={`catalog-search-${isForwardCurves ? 'forward-curves' : 'spot'}`} />
    </Box>
  );
};

export default CatalogToolbar;
